import React, { useState, useEffect, useRef } from 'react';
import {
  Activity, AlertCircle, CheckCircle, XCircle, Clock,
  TrendingUp, BarChart,
  Filter, Download, RefreshCw, Pause, Play,
  Terminal, Bug, Info,
  AlertTriangle, Search
} from 'lucide-react';

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warning' | 'error' | 'success' | 'debug';
  nodeId?: string;
  nodeName?: string;
  message: string;
  details?: any;
  duration?: number;
}

export interface FlowMetrics {
  totalExecutions: number;
  successRate: number;
  averageDuration: number;
  errorCount: number;
  warningCount: number;
  lastExecution?: Date;
  nodeMetrics: Map<string, {
    executions: number;
    avgDuration: number;
    errors: number;
  }>;
}

interface FlowMonitoringProps {
  logs: LogEntry[];
  metrics?: FlowMetrics;
  isRunning?: boolean;
  autoScroll?: boolean;
  maxLogs?: number;
  onClearLogs?: () => void;
  onExportLogs?: (format: 'json' | 'csv' | 'txt') => void;
  onFilterChange?: (filters: LogFilters) => void;
  onPause?: () => void;
  onResume?: () => void;
}

interface LogFilters {
  levels: Set<string>;
  search: string;
  startDate?: Date;
  endDate?: Date;
  nodeId?: string;
}

export const FlowMonitoring: React.FC<FlowMonitoringProps> = ({
  logs = [],
  metrics,
  isRunning = false,
  autoScroll = true,
  maxLogs = 1000,
  onClearLogs,
  onExportLogs,
  onFilterChange,
  onPause,
  onResume
}) => {
  const [filters, setFilters] = useState<LogFilters>({
    levels: new Set(['info', 'warning', 'error', 'success', 'debug']),
    search: ''
  });
  const [view, setView] = useState<'logs' | 'metrics' | 'timeline'>('logs');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'info':
        return <Info size={12} color="#3B82F6" />;
      case 'warning':
        return <AlertTriangle size={12} color="#F59E0B" />;
      case 'error':
        return <XCircle size={12} color="#EF4444" />;
      case 'success':
        return <CheckCircle size={12} color="#10B981" />;
      case 'debug':
        return <Bug size={12} color="#8B5CF6" />;
      default:
        return <Terminal size={12} color="#6B7280" />;
    }
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'info': return '#3B82F6';
      case 'warning': return '#F59E0B';
      case 'error': return '#EF4444';
      case 'success': return '#10B981';
      case 'debug': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const filteredLogs = logs.filter(log => {
    if (!filters.levels.has(log.level)) return false;
    if (filters.search && !log.message.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.nodeId && log.nodeId !== filters.nodeId) return false;
    if (filters.startDate && new Date(log.timestamp) < filters.startDate) return false;
    if (filters.endDate && new Date(log.timestamp) > filters.endDate) return false;
    return true;
  });

  const logStats = {
    total: filteredLogs.length,
    info: filteredLogs.filter(l => l.level === 'info').length,
    warning: filteredLogs.filter(l => l.level === 'warning').length,
    error: filteredLogs.filter(l => l.level === 'error').length,
    success: filteredLogs.filter(l => l.level === 'success').length,
    debug: filteredLogs.filter(l => l.level === 'debug').length
  };

  const handleLevelToggle = (level: string) => {
    const newLevels = new Set(filters.levels);
    if (newLevels.has(level)) {
      newLevels.delete(level);
    } else {
      newLevels.add(level);
    }
    const newFilters = { ...filters, levels: newLevels };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#f8f9fa',
      color: '#1a202c'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        background: '#ffffff'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Activity size={16} />
            Monitoramento do Fluxo
            {isRunning && (
              <span style={{
                padding: '2px 6px',
                background: '#10B981',
                borderRadius: '10px',
                fontSize: '10px',
                animation: 'pulse 2s infinite'
              }}>
                Executando
              </span>
            )}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isRunning ? (
              <button
                onClick={onPause}
                style={{
                  padding: '6px',
                  background: '#EF4444',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <Pause size={14} />
              </button>
            ) : (
              <button
                onClick={onResume}
                style={{
                  padding: '6px',
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <Play size={14} />
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '6px',
                background: showFilters ? '#3B82F6' : '#f1f5f9',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: showFilters ? '#ffffff' : '#374151',
                cursor: 'pointer'
              }}
            >
              <Filter size={14} />
            </button>
            <button
              onClick={onClearLogs}
              style={{
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={() => onExportLogs?.('json')}
              style={{
                padding: '6px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              <Download size={14} />
            </button>
          </div>
        </div>

        {/* View Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          {(['logs', 'metrics', 'timeline'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                flex: 1,
                padding: '6px',
                background: view === v ? '#3B82F6' : '#f1f5f9',
                border: `1px solid ${view === v ? '#3B82F6' : '#d1d5db'}`,
                borderRadius: '6px',
                color: view === v ? '#ffffff' : '#374151',
                fontSize: '11px',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {v === 'logs' && <Terminal size={12} style={{ display: 'inline', marginRight: '4px' }} />}
              {v === 'metrics' && <BarChart size={12} style={{ display: 'inline', marginRight: '4px' }} />}
              {v === 'timeline' && <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />}
              {v === 'logs' ? 'Logs' : v === 'metrics' ? 'Métricas' : 'Timeline'}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={{
          padding: '12px',
          borderBottom: '1px solid #d1d5db',
          background: '#f9fafb'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                type="text"
                placeholder="Buscar nos logs..."
                value={filters.search}
                onChange={(e) => {
                  const newFilters = { ...filters, search: e.target.value };
                  setFilters(newFilters);
                  onFilterChange?.(newFilters);
                }}
                style={{
                  width: '100%',
                  padding: '6px 6px 6px 32px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            {(['info', 'warning', 'error', 'success', 'debug'] as const).map(level => (
              <button
                key={level}
                onClick={() => handleLevelToggle(level)}
                style={{
                  padding: '4px 8px',
                  background: filters.levels.has(level) ? getLevelColor(level) : '#f1f5f9',
                  border: `1px solid ${filters.levels.has(level) ? getLevelColor(level) : '#d1d5db'}`,
                  borderRadius: '4px',
                  color: filters.levels.has(level) ? '#ffffff' : '#374151',
                  fontSize: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  textTransform: 'capitalize'
                }}
              >
                {getLevelIcon(level)}
                {level}
                <span style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '1px 4px',
                  borderRadius: '10px'
                }}>
                  {logStats[level]}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {view === 'logs' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px',
          fontFamily: 'monospace',
          fontSize: '11px'
        }}>
          {filteredLogs.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              color: '#9ca3af'
            }}>
              <Terminal size={40} style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '12px', fontFamily: 'sans-serif' }}>
                Nenhum log disponível
              </p>
            </div>
          ) : (
            <>
              {filteredLogs.map(log => (
                <div
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  style={{
                    padding: '8px',
                    marginBottom: '4px',
                    background: selectedLog?.id === log.id ? '#f1f5f9' : 'transparent',
                    borderLeft: `3px solid ${getLevelColor(log.level)}`,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.background = selectedLog?.id === log.id ? '#f1f5f9' : 'transparent'}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    {getLevelIcon(log.level)}
                    <span style={{
                      color: '#9ca3af',
                      minWidth: '80px'
                    }}>
                      {formatTimestamp(log.timestamp)}
                    </span>
                    {log.nodeName && (
                      <span style={{
                        color: '#3B82F6',
                        minWidth: '100px',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        [{log.nodeName}]
                      </span>
                    )}
                    <span style={{
                      flex: 1,
                      color: '#374151',
                      wordBreak: 'break-word'
                    }}>
                      {log.message}
                    </span>
                    {log.duration !== undefined && (
                      <span style={{
                        color: '#10B981',
                        marginLeft: 'auto'
                      }}>
                        {formatDuration(log.duration)}
                      </span>
                    )}
                  </div>
                  {log.details && selectedLog?.id === log.id && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px',
                      background: '#ffffff',
                      borderRadius: '4px',
                      color: '#6b7280',
                      fontSize: '10px'
                    }}>
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
              <div ref={logsEndRef} />
            </>
          )}
        </div>
      )}

      {view === 'metrics' && metrics && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px'
        }}>
          {/* Overall Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #d1d5db'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <Activity size={16} color="#3B82F6" />
                <span style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  Total Execuções
                </span>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#374151'
              }}>
                {metrics.totalExecutions}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #d1d5db'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <TrendingUp size={16} color="#10B981" />
                <span style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  Taxa de Sucesso
                </span>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#10B981'
              }}>
                {metrics.successRate.toFixed(1)}%
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #d1d5db'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <Clock size={16} color="#F59E0B" />
                <span style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  Tempo Médio
                </span>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#374151'
              }}>
                {formatDuration(metrics.averageDuration)}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #d1d5db'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} color="#EF4444" />
                <span style={{
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  Erros
                </span>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#EF4444'
              }}>
                {metrics.errorCount}
              </div>
            </div>
          </div>

          {/* Node Metrics */}
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '12px',
            fontWeight: 600,
            color: '#6b7280'
          }}>
            Métricas por Nó
          </h4>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {Array.from(metrics.nodeMetrics.entries()).map(([nodeId, nodeMetric]) => (
              <div
                key={nodeId}
                style={{
                  padding: '10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#374151'
                  }}>
                    {nodeId}
                  </span>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    fontSize: '10px',
                    color: '#6b7280'
                  }}>
                    <span>Execuções: {nodeMetric.executions}</span>
                    <span>Tempo: {formatDuration(nodeMetric.avgDuration)}</span>
                    {nodeMetric.errors > 0 && (
                      <span style={{ color: '#EF4444' }}>
                        Erros: {nodeMetric.errors}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'timeline' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px'
        }}>
          <div style={{
            position: 'relative',
            paddingLeft: '40px'
          }}>
            {/* Timeline Line */}
            <div style={{
              position: 'absolute',
              left: '20px',
              top: 0,
              bottom: 0,
              width: '2px',
              background: '#d1d5db'
            }} />

            {/* Timeline Events */}
            {filteredLogs.map((log, index) => (
              <div
                key={log.id}
                style={{
                  position: 'relative',
                  marginBottom: '20px'
                }}
              >
                {/* Timeline Dot */}
                <div style={{
                  position: 'absolute',
                  left: '-26px',
                  top: '8px',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: getLevelColor(log.level),
                  border: '2px solid #ffffff'
                }} />

                {/* Event Card */}
                <div style={{
                  padding: '10px',
                  background: '#f9fafb',
                  border: `1px solid ${getLevelColor(log.level)}`,
                  borderRadius: '6px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    {getLevelIcon(log.level)}
                    <span style={{
                      fontSize: '10px',
                      color: '#9ca3af'
                    }}>
                      {formatTimestamp(log.timestamp)}
                    </span>
                    {log.nodeName && (
                      <span style={{
                        fontSize: '10px',
                        color: '#3B82F6'
                      }}>
                        {log.nodeName}
                      </span>
                    )}
                    {log.duration !== undefined && (
                      <span style={{
                        fontSize: '10px',
                        color: '#10B981',
                        marginLeft: 'auto'
                      }}>
                        {formatDuration(log.duration)}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#374151'
                  }}>
                    {log.message}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid #d1d5db',
        background: '#f9fafb',
        fontSize: '10px',
        color: '#9ca3af',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <span>Total: {logStats.total} logs</span>
          <span>Filtrados: {filteredLogs.length}</span>
          {metrics?.lastExecution && (
            <span>
              Última execução: {new Date(metrics.lastExecution).toLocaleString('pt-BR')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: isRunning ? '#10B981' : '#6B7280',
            display: 'inline-block',
            animation: isRunning ? 'pulse 2s infinite' : 'none'
          }} />
          <span>{isRunning ? 'Executando' : 'Parado'}</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default FlowMonitoring;
